import { useCMSPageField } from '@/cms/fields'
import { getBrandName } from '@/scopes/common/infrastructure/analytics/brandNameMapper'
import ga4analyticsClient from '@/scopes/common/infrastructure/analytics/ga4analyticsClient'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useCommonDependency } from '@/scopes/common/infrastructure/dependencies/common'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { createBrowserQueryStringKeepingParams } from '@/scopes/common/infrastructure/query-strings/browserQueryStringKeepingParams'
import { createQueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import { useAccommodationContext } from '@/scopes/prices/components/AccommodationContext'
import { PricesUseCasesContext } from '@/scopes/prices/components/PricesUseCasesContext'
import { pricesUseCases } from '@/scopes/prices/domain/useCases/pricesUseCases'
import { createAvailabilityApiClient } from '@/scopes/prices/infrastructure/availability/availabilityApiClient'
import { createFiltersApiClient } from '@/scopes/prices/infrastructure/filters/filtersAPIClient'
import {
  createPricesFiltersParserWithInitState,
} from '@/scopes/prices/infrastructure/parser/pricesFilterParserWithInitState'
import { createPricesFiltersParser } from '@/scopes/prices/infrastructure/parser/pricesFiltersParser'
import { createPricesApiClient } from '@/scopes/prices/infrastructure/pricesByDuration/pricesAPIClient'
import { createPromotedPriceApiClient } from '@/scopes/prices/infrastructure/promoted-price/promotedPriceApiClient'

import {
  createPricesQueryParametersToStateMapper,
} from '@/scopes/prices/infrastructure/queryParametersToStateMapper/pricesQueryParametersToStateMapper'
import { createRoomSelectorApiClient } from '@/scopes/prices/infrastructure/rooms/roomSelectorApiClient'
import { newPricesStore } from '@/scopes/prices/infrastructure/store'
import { createFlightSelectorApiClient } from '@/scopes/prices/infrastructure/transport/flight/flightSelectorApiClient'
import { createSelfDriveSelectorApiClient } from '@/scopes/prices/infrastructure/transport/selfdrive/selfDriveSelectorApiClient'
import { useDecision } from '@optimizely/react-sdk'
import * as React from 'react'

interface PricesUseCasesContextProviderProps {
  children: React.ReactNode
}

export function PricesUseCasesContextProvider({ children }: PricesUseCasesContextProviderProps) {
  const { contextItemId, accoId, bookingGateId, promotedPriceId } = useAccommodationContext()
  const brandName = useConfiguration('siteName')
  const accoBrand = getBrandName(brandName)
  const queryStringClient = useCommonDependency('queryStringClient')
  const analytics = useCMSPageField('PDP.pricetable.analytics')
  const [priceTableByWeeksDecision] = useDecision('ID_306_price_table___show_dates_around_picked_date')
  const abByWeeksEnabled = useFeatureFlag('ENABLE_AB_BYWEEKS')
  const priceTableByWeeks = (priceTableByWeeksDecision?.enabled ?? false) && abByWeeksEnabled
  const variationKey = priceTableByWeeksDecision?.variationKey
  const deepLinkingEnabled = useFeatureFlag('ENABLE_DEEPLINKING')

  const { initPrices, initPriceTable, selectFilterMonth, selectFilter, selectRange, selectPrice, selectRoom, setOutboundRouteId, setInboundRouteId, resetPrices }
    = pricesUseCases({
      filtersApiClient: createFiltersApiClient({}),
      pricesApiClient: createPricesApiClient({}),
      roomSelectorApiClient: createRoomSelectorApiClient({}),
      flightSelectorApiClient: createFlightSelectorApiClient({}),
      selfDriveSelectorApiClient: createSelfDriveSelectorApiClient({}),
      analyticsClient: ga4analyticsClient,
      store: newPricesStore,
      queryStringSerializer: createQueryStringSerializer({
        parser: deepLinkingEnabled ? createPricesFiltersParserWithInitState() : createPricesFiltersParser(),
      }),
      queryParametersToStateMapper: createPricesQueryParametersToStateMapper(deepLinkingEnabled),
      browserQueryString: createBrowserQueryStringKeepingParams({}),
      promotedPriceAPIClient: createPromotedPriceApiClient({}),
      isAccoAvailableApiClient: createAvailabilityApiClient({}),
    })
  const initialize = React.useCallback(async (priceTableByWeeks: boolean) => {
    const originList = queryStringClient.getValue('originList')
    await initPrices({
      accoId,
      bookingGateId,
      contextItemId,
      analytics: analytics ? { ...analytics, originList: originList || undefined, accoBrand } : null,
      initialFilterSelection: {
        Participants: [
          {
            adults: 2,
            children: [],
            babies: [],
          },
        ],
      },
      promotedPriceId,
      priceTableByWeeks,
    })
  }, [accoId, bookingGateId, contextItemId, priceTableByWeeks])

  React.useEffect(() => {
    if (variationKey && abByWeeksEnabled) {
      initialize(priceTableByWeeks)
    }
  }, [variationKey, abByWeeksEnabled])

  React.useEffect(() => {
    if (!abByWeeksEnabled) {
      initialize(false)
    }
  }, [abByWeeksEnabled])

  return (
    <PricesUseCasesContext.Provider
      value={{
        initPrices,
        initPriceTable,
        selectFilterMonth,
        selectFilter,
        selectRange,
        selectPrice,
        selectRoom,
        setOutboundRouteId,
        setInboundRouteId,
        resetPrices,
      }}
    >
      {children}
    </PricesUseCasesContext.Provider>
  )
}
